import React, { useMemo, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Checkbox, ClickAwayListener, Grid, Paper, Popover } from '@mui/material';
import {
  EditOutlined,
  VisibilityOutlined,
  CloudDownloadOutlined,
  MoreHorizOutlined,
  HistoryOutlined,
  CompareArrowsOutlined,
  DeleteOutlined,
  DeleteForeverOutlined,
  DeleteSweepOutlined
} from '@mui/icons-material';
import { Autocomplete, IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import UploadDocumentComponent from './UploadDocumentComponent';
import { CardTitle, UploadDocumentGrid } from './DocumentsStyles';
import { RightsContext } from 'providers/RightsContext';

const DocumentsUploadCardComponent = React.memo(
  ({
    index,
    disabled,
    onDelete,
    onDeleteFiles,
    document,
    validationInfo,
    dirtyInfo,
    onDocumentUploadHistoryClick,
    onChangeStateTransitionClick,
    onDocumentsUploadTransitionsClick,
    readOnlyFull,
    downloadClick,
    uploadClick,
    filesUploadInProgress,
    onEditClick,
    onSelectDocument,
    onPreviewClick
  }) => {
    const { t } = useTranslation();
    const [openOfferActions, setOpenOfferActions] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const { uploadDeleteDocuments, uploadDocumentWrite, deleteMyUploadedFiles, deleteAllUploadedFiles } = useContext(RightsContext);

    const handleOpenOfferActions = useCallback(event => {
      setOpenOfferActions(true);
      setAnchorEl(event.currentTarget);
    }, []);

    const handleCloseOfferActions = useCallback(() => setOpenOfferActions(false), []);

    const availableTransitions = useMemo(
      () => document?.availableTransitions.filter(x => x.initialStateId === document.stateId),
      [document]
    );
    const disabledButtons = useMemo(() => {
      const isUploadInProgress = filesUploadInProgress.some(
        attachment => attachment.attachmentId === document.attachmentId && attachment.inProgress === true
      );

      return disabled || isUploadInProgress;
    }, [disabled, document.attachmentId, filesUploadInProgress]);
    const disabledDeleteFileButtons = useMemo(() => {
      return disabledButtons || !document.isUploaded || !document.canDeleteFile || document.isExternal;
    }, [disabledButtons, document.canDeleteFile, document.isExternal, document.isUploaded]);

    const handleCheckChange = useCallback(() => {
      onSelectDocument(document.attachmentId);
    }, [document.attachmentId, onSelectDocument]);

    const getCustomTextField = useCallback(params => {
      const { endAdornment, ...rest } = params.InputProps;
      return (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: '14px', fontWeight: 'bold', paddingTop: 0, marginTop: '-5px' }
          }}
          InputProps={{
            ...rest,
            sx: theme => ({
              height: theme.spacing(3),
              maxWidth: theme.spacing(25)
            })
          }}
          endAdornment={endAdornment}
          variant='outlined'
        />
      );
    }, []);

    return (
      <Grid container spacing={2} display='flex' alignItems='center'>
        <Grid item>
          <Checkbox checked={document.isSelected} onChange={handleCheckChange} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Grid container display='flex' justifyContent='center'>
            <UploadDocumentGrid item xs={12} lg='auto'>
              <UploadDocumentComponent
                onUpload={uploadClick}
                isUploaded={document.isUploaded}
                filesUploadInProgress={filesUploadInProgress}
                attachmentId={document.attachmentId}
                readOnly={
                  disabled || document.isExternal || readOnlyFull || !validationInfo.isValid || dirtyInfo.isDirty || !uploadDocumentWrite
                }
                disabled={!document.canUpload}
              />
            </UploadDocumentGrid>
            <Grid item xs={12} lg zeroMinWidth>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container alignItems='center'>
                    <Grid item xs={12} lg='auto'>
                      <CardTitle noWrap tooltip={document.attachmentTypeName || ''}>
                        {(document.isMandatory ? '* ' : '') + document.attachmentTypeName}
                      </CardTitle>
                    </Grid>
                    <Grid item xs={12} lg>
                      <Typography
                        variant='subtitle2'
                        noWrap
                        tooltip={
                          document.userName
                            ? t('OfferEdit.Documents.Labels.UploadedBy', {
                                userName: document.userName,
                                date: t('DATE', { date: document.uploadDate })
                              })
                            : ''
                        }
                      >
                        {document.userName
                          ? t('OfferEdit.Documents.Labels.UploadedBy', {
                              userName: document.userName,
                              date: t('DATE', { date: document.uploadDate })
                            })
                          : ''}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6} lg={4} xl={3} fontSize={16} lineHeight={1.4}>
                  <Grid container spacing={1}>
                    <Grid item xs='auto'>
                      {t('OfferEdit.Documents.Labels.Status')}
                    </Grid>
                    <Grid item xs py={0} px={0.5}>
                      <Autocomplete
                        id='docAttachment_state'
                        valueKey='finalStateId'
                        labelKey='finalStateName'
                        value={{
                          finalStateId: document.stateId,
                          finalStateName: document.stateName
                        }}
                        onChange={onChangeStateTransitionClick}
                        options={availableTransitions}
                        disabled={
                          disabled ||
                          dirtyInfo.isDirty ||
                          readOnlyFull ||
                          !uploadDocumentWrite ||
                          !availableTransitions ||
                          availableTransitions.length === 0
                        }
                        renderInput={getCustomTextField}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6} lg={8} xl={9}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} fontSize={16} lineHeight={1.4}>
                      <Grid container spacing={1}>
                        <Grid item xs='auto'>
                          {t('OfferEdit.Documents.Labels.Comments')}
                        </Grid>
                        <Grid item xs zeroMinWidth>
                          <Typography fontSize={16} fontWeight='bold' lineHeight={1.4} noWrap tooltip={document.comments || ''}>
                            {document.comments || '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        {document.canExpire && (
                          <Grid item xs={12} lg={6} xl={4} fontSize={16} lineHeight={1.4}>
                            <Grid container spacing={1}>
                              <Grid item xs='auto'>
                                {t('OfferEdit.Documents.Labels.ValidityDate')}
                              </Grid>
                              <Grid item xs zeroMinWidth>
                                <Typography fontSize={16} fontWeight='bold' lineHeight={1.4} noWrap>
                                  {document.valabilityDate ? t('DATE', { date: document.valabilityDate }) : '-'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}
                        {document?.attachmentProperties?.map(item => {
                          return !item.propertyVisible ? (
                            ''
                          ) : (
                            <Grid key={index} item xs={12} lg={6} xl={4} fontSize={16} lineHeight={1.4}>
                              <Grid container spacing={1}>
                                <Grid item xs='auto'>
                                  {item.propertyName}:{' '}
                                </Grid>
                                <Grid item xs zeroMinWidth>
                                  <Typography
                                    fontSize={16}
                                    fontWeight='bold'
                                    lineHeight={1.4}
                                    noWrap
                                    tooltip={item.propertyValue && item.propertyTypeCode !== 'TP_DATE' ? item.propertyValue : ''}
                                  >
                                    {item.propertyValue
                                      ? item.propertyTypeCode === 'TP_DATE'
                                        ? t('DATE', { date: item.propertyValue })
                                        : item.propertyValue
                                      : '-'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={1} lg='auto' justifyContent='center' minWidth={72}>
          <Grid container>
            <Grid item xs={12} lg={3}>
              <IconButton
                id={'DocumentAttachmentEditBtn_' + index}
                variant='text'
                color='default'
                onClick={onEditClick}
                disabled={disabledButtons || document.isExternal}
                tooltip={t('OfferEdit.Documents.Edit.Edit')}
              >
                <EditOutlined />
              </IconButton>
            </Grid>
            <Grid item xs={12} lg={3}>
              <IconButton
                id={'DocumentAttachmentViewBtn_' + index}
                variant='text'
                color='default'
                onClick={onPreviewClick}
                disabled={disabledButtons || !document.isUploaded}
                tooltip={t('OfferEdit.Documents.View')}
              >
                <VisibilityOutlined />
              </IconButton>
            </Grid>
            <Grid item xs={12} lg={3}>
              <IconButton
                id={'DocumentAttachmentsDownloadBtn_' + index}
                variant='text'
                color='default'
                onClick={downloadClick}
                disabled={disabledButtons || !document.isUploaded || !document.canDownload}
                tooltip={
                  document.canDownload
                    ? !document.isUploaded && document.isDeleted
                      ? t('Tooltips.Deleted')
                      : t('Tooltips.Download')
                    : t('Tooltips.DownloadForbidden')
                }
              >
                <CloudDownloadOutlined color={!document.isUploaded && document.isDeleted ? 'error' : 'inherit'} />
              </IconButton>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Popover
                open={openOfferActions}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
              >
                <ClickAwayListener onClickAway={handleCloseOfferActions}>
                  <Paper>
                    {openOfferActions && (
                      <Grid container>
                        <Grid item xs>
                          <IconButton
                            id={'DocumentAttachmentDeleteBtn_' + index}
                            variant='text'
                            color='default'
                            onClick={onDelete(document.attachmentId, document.fileId)}
                            disabled={document.isMandatory || document.isExternal || !uploadDeleteDocuments || !uploadDocumentWrite}
                            tooltip={t('Delete')}
                          >
                            <DeleteForeverOutlined />
                          </IconButton>
                        </Grid>
                        {deleteMyUploadedFiles && (
                          <Grid item xs>
                            <IconButton
                              id={'DocumentAttachmentDeleteMyVersionsBtn_' + index}
                              variant='text'
                              color='default'
                              onClick={() => onDeleteFiles(document.attachmentId, document.fileId, true, false)}
                              disabled={disabledDeleteFileButtons}
                              tooltip={t('Tooltips.DeleteMyVersions')}
                            >
                              <DeleteOutlined />
                            </IconButton>
                          </Grid>
                        )}
                        {deleteAllUploadedFiles && (
                          <Grid item xs>
                            <IconButton
                              id={'DocumentAttachmentDeleteAllVersionsBtn_' + index}
                              variant='text'
                              color='default'
                              onClick={() => onDeleteFiles(document.attachmentId, document.fileId, false, true)}
                              disabled={disabledDeleteFileButtons}
                              tooltip={t('Tooltips.DeleteAllVersions')}
                            >
                              <DeleteSweepOutlined />
                            </IconButton>
                          </Grid>
                        )}
                        <Grid item xs>
                          <IconButton
                            id={'DocumentAttachmentsUploadHistoryBtn_' + index}
                            variant='text'
                            color='default'
                            onClick={onDocumentUploadHistoryClick}
                            disabled={!document.isUploaded && !document.isDeleted}
                            tooltip={t('Tooltips.DocumentHistory')}
                          >
                            <HistoryOutlined />
                          </IconButton>
                        </Grid>

                        <Grid item xs>
                          <IconButton
                            id={'DocumentAttachmentTransitionsHistoryBtn_' + index}
                            variant='text'
                            color='default'
                            onClick={onDocumentsUploadTransitionsClick}
                            tooltip={t('Tooltips.TransitionsHistory')}
                          >
                            <CompareArrowsOutlined />
                          </IconButton>
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </ClickAwayListener>
              </Popover>
              {!openOfferActions ? (
                <IconButton
                  id={'offerHeader_actionList_showBtn'}
                  variant='text'
                  color='default'
                  onClick={handleOpenOfferActions}
                  disabled={disabledButtons}
                  tooltip={t('Tooltips.ShowHeaderActions')}
                >
                  <MoreHorizOutlined />
                </IconButton>
              ) : (
                <IconButton
                  id={'offerHeader_actionList_hideBtn'}
                  variant='text'
                  color='default'
                  onClick={handleCloseOfferActions}
                  tooltip={t('Tooltips.HideHeaderActions')}
                >
                  <MoreHorizOutlined />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

DocumentsUploadCardComponent.displayName = 'DocumentsUploadCardComponent';

DocumentsUploadCardComponent.propTypes = {
  index: PropTypes.any,
  onDelete: PropTypes.func.isRequired,
  onDeleteFiles: PropTypes.func.isRequired,
  document: PropTypes.object.isRequired,
  validationInfo: PropTypes.object.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  onDocumentUploadHistoryClick: PropTypes.func,
  onChangeStateTransitionClick: PropTypes.func,
  readOnlyFull: PropTypes.bool.isRequired,
  onDocumentsUploadTransitionsClick: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  uploadClick: PropTypes.func.isRequired,
  downloadClick: PropTypes.func.isRequired,
  filesUploadInProgress: PropTypes.array.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onSelectDocument: PropTypes.func.isRequired,
  onPreviewClick: PropTypes.func.isRequired
};

export default DocumentsUploadCardComponent;
